import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMenu as Menu, FiX as X, FiUser as User } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import Logo from '../common/Logo';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetQuote = () => navigate('/quote');

  const navItems = [
    { label: 'Services', href: '/services' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' }
  ];

  const dashboardLink = '/dashboard';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-200 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <Logo className="h-12 w-auto group-hover:scale-105 transition-transform duration-200" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {!isAuthenticated && navItems.map((item) => (
              <a 
                key={item.label} 
                href={item.href} 
                className="text-gray-700 hover:text-[#DC143C] font-medium transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <Link 
                  to={dashboardLink}
                  className="bg-[#0038A8] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#002d8a] transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Dashboard
                </Link>
                <button 
                  onClick={() => { logout(); navigate('/'); }}
                  className="hidden md:block text-gray-600 hover:text-red-600 font-medium"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <>
                <button 
                  onClick={handleGetQuote}
                  className="bg-[#DC143C] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#B01030] transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  Get Quote
                </button>
                <Link 
                  to="/login"
                  className="text-[#0038A8] border border-[#0038A8] px-6 py-2 rounded-lg font-semibold hover:bg-[#0038A8] hover:text-white transition-all duration-200"
                >
                  Client Login
                </Link>
              </>
            )}

            {/* Mobile menu button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 animate-fadeIn">
            <div className="flex flex-col space-y-4">
              {!isAuthenticated && navItems.map((item) => (
                <a 
                  key={item.label} 
                  href={item.href} 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-700 hover:text-[#DC143C] font-medium py-2 transition-colors duration-200"
                >
                  {item.label}
                </a>
              ))}
              {isAuthenticated && (
                <div className="py-2 border-t border-gray-100 mt-2 pt-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <User size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{user?.firstName} {user?.lastName}</div>
                      <div className="text-xs text-gray-500">Client</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => { logout(); navigate('/'); setIsMobileMenuOpen(false); }}
                    className="w-full text-left text-red-600 font-medium"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;